
import React, { useState, useEffect, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Overlay from 'ol/Overlay';
import { fromLonLat, toLonLat } from 'ol/proj';
import 'ol/ol.css';

// OpenLayers map setup with marker
const initializeMap = (mapRef, setSelectedPosition, fetchAddressFromCoordinates, initialPosition) => {
  const map = new Map({
    target: mapRef.current,
    layers: [
      new TileLayer({
        source: new OSM(),
      }),
    ],
    view: new View({
      center: initialPosition ? fromLonLat([initialPosition[1], initialPosition[0]]) : fromLonLat([-0.09, 51.505]), // Use current location or default to London
      zoom: 13,
    }),
  });

  // Create marker overlay
  const marker = new Overlay({
    position: initialPosition ? fromLonLat([initialPosition[1], initialPosition[0]]) : undefined,
    positioning: 'center-center',
    element: document.createElement('div'),
    stopEvent: false,
  });
  marker.getElement().style.backgroundColor = 'red';
  marker.getElement().style.width = '12px';
  marker.getElement().style.height = '12px';
  marker.getElement().style.borderRadius = '50%';
  marker.getElement().style.border = '2px solid white';
  map.addOverlay(marker);

  // Update marker position on map click
  map.on('click', (event) => {
    const coords = toLonLat(event.coordinate);
    const [lng, lat] = coords;
    setSelectedPosition([lat, lng]);
    marker.setPosition(fromLonLat([lng, lat]));
    fetchAddressFromCoordinates(lat, lng);
  });

  return { map, marker };
};

const apiUrl = import.meta.env.VITE_API_URL;

// Error Boundary to catch map rendering errors
class MapErrorBoundary extends Component {
  state = { hasError: false, errorMessage: '' };

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Map rendering error:', error, errorInfo);
    console.error('Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div>
          <p>{this.props.t('Failed to load map. Error:')} {this.state.errorMessage}</p>
          <Button onClick={() => this.setState({ hasError: false, errorMessage: '' })}>
            {this.props.t('Retry')}
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AddAddress({ show, onHide, addressData, setAddressData, token }) {
  const { t } = useTranslation();
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const mapRef = React.useRef(null);

  // Get current location
  useEffect(() => {
    if (showMapModal && !currentLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setCurrentLocation([latitude, longitude]);
            setSelectedPosition([latitude, longitude]);
            fetchAddressFromCoordinates(latitude, longitude); // Auto-fill address
          },
          (error) => {
            console.error('Geolocation error:', error);
            toast.warn(t('Unable to get current location. Using default location.'));
          },
          { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
      } else {
        toast.warn(t('Geolocation not supported by your browser.'));
      }
    }
  }, [showMapModal]);

  // Initialize map when modal opens
  useEffect(() => {
    setIsMapReady(true);
    let mapInstance;
    if (showMapModal && mapRef.current) {
      const { map, marker } = initializeMap(mapRef, setSelectedPosition, fetchAddressFromCoordinates, currentLocation);
      mapInstance = map;
      // Update marker if current location changes
      if (currentLocation) {
        marker.setPosition(fromLonLat([currentLocation[1], currentLocation[0]]));
      }
    }
    return () => {
      if (mapInstance) {
        mapInstance.setTarget(null); // Clean up map on unmount
      }
      setIsMapReady(false);
    };
  }, [showMapModal, currentLocation]);

  // Handle input changes for the form
  const handleInputChangeAddress = (e) => {
    const { name, value } = e.target;
    setAddressData((prevState) => ({
      ...prevState,
      [name]: value || '',
    }));
  };

  // Handle form submission
  const handleFormSubmitAddress = (e) => {
    e.preventDefault();

    const formDataAdress = new FormData();

    Object.keys(addressData).forEach((key) => {
      formDataAdress.append(key, addressData[key] || '');
    });

    const url = addressData.id
      ? `${apiUrl}/api/edit-address/${addressData.id}`
      : `${apiUrl}/api/add-address`;

    axios
      .post(url, formDataAdress, {
        headers: {
          Authorization: `Bearer ${token?.token}`,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        const message = addressData.id ? t('Address updated successfully') : t('Address added successfully');
        toast.success(message);
        setAddressData(response.data);
        onHide();
      })
      .catch((error) => {
        console.error(error);
        const message = addressData.id ? t('Failed to update address') : t('Failed to add address');
        toast.error(message);
      });
  };

  // Reverse geocoding using Nominatim
  const fetchAddressFromCoordinates = async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        {
          headers: { 'User-Agent': 'Regaleyes/1.0' },
        }
      );
      const address = response.data.address;

      setAddressData((prevState) => ({
        ...prevState,
        address: address.road || address.street || `${lat}, ${lng}`,
        country: address.country || '',
        state: address.state || address.region || '',
        city: address.city || address.town || address.village || '',
        street: address.road || address.street || '',
        appartment: address.house_number || '',
        zip: address.postcode || '',
      }));
    } catch (error) {
      console.error('Failed to fetch address:', error);
      toast.error(t('Failed to fetch address from location'));
    }
  };

  // Handle map modal close and save
  const handleMapSave = () => {
    setShowMapModal(false);
  };

  return (
    <>
      <Modal show={show} onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title>{t('Add Address')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFormSubmitAddress}>
            <Form.Group controlId="formAddress" className="mb-3">
              <Form.Label>{t('Address')}</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={addressData.address}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="formCountry" className="mb-3">
              <Form.Label>{t('Country')}</Form.Label>
              <Form.Control
                type="text"
                name="country"
                value={addressData.country}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="formState" className="mb-3">
              <Form.Label>{t('State')}</Form.Label>
              <Form.Control
                type="text"
                name="state"
                value={addressData.state}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="formCity" className="mb-3">
              <Form.Label>{t('City')}</Form.Label>
              <Form.Control
                type="text"
                name="city"
                value={addressData.city}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="formStreet" className="mb-3">
              <Form.Label>{t('Street')}</Form.Label>
              <Form.Control
                type="text"
                name="street"
                value={addressData.street}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="formAppartment" className="mb-3">
              <Form.Label>{t('Apartment no')}</Form.Label>
              <Form.Control
                type="text"
                name="appartment"
                value={addressData.appartment}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Form.Group controlId="formZip" className="mb-3">
              <Form.Label>{t('Zip code')}</Form.Label>
              <Form.Control
                type="text"
                name="zip"
                value={addressData.zip}
                onChange={handleInputChangeAddress}
              />
            </Form.Group>

            <Button
              variant="dark"
              className="btn btn-dark p-2 m-2"
              onClick={() => setShowMapModal(true)}
            >
              {t('Select Location by Map')}
            </Button>

            <Button variant="primary" type="submit" className="btn btn-dark p-2 m-2">
              {t('Save Changes')}
            </Button>
            <Button onClick={onHide} className="btn btn-secondary p-2 m-2">
              {t('Cancel')}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {isMapReady && (
        <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{t('Select Location')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <MapErrorBoundary t={t}>
              <div
                ref={mapRef}
                style={{ height: '400px', width: '100%' }}
              />
            </MapErrorBoundary>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMapModal(false)}>
              {t('Cancel')}
            </Button>
            <Button variant="primary" onClick={handleMapSave}>
              {t('Save Location')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}